import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCampaignAudience,
  sendCampaign,
  checkCampaignDevices,
  getCampaignRun,
  resumeCampaignRun,
  getParentCampaignHistory,
  setParentMute,
} from '../../Services/arndAPI';
import { playKaching } from './kaching';
import ReachIndicators, { ReachLegend } from './ReachIndicators';
import styles from './Styles/EventOps.module.css';
import { appLink, isLeadOnly } from './campaignLength';
import MessageEditor from './campaign/MessageEditor';
import ChannelPicker from './campaign/ChannelPicker';
import { CHANNELS, CHANNEL_ICON, CHANNEL_LABEL, STAGE_LABELS } from './campaign/placeholders';

// The builder also opens from the Purchased card, which the automated
// campaigns never target.
const BUILDER_STAGE_LABELS = { ...STAGE_LABELS, purchased: 'Purchased' };

const EXCLUDED_REASON = {
  manual: 'removed by you',
};

// A run still 'running' with no progress for this long has stopped (the API's
// STALE_MS) — the banner offers Resume instead of spinning forever.
const RUN_STALE_MS = 2 * 60 * 1000;
const POLL_MS = 3000;
// A finished run that ended early can be picked up where it stopped.
const RESUMABLE = new Set(['stopped', 'failed']);

/** Will this parent actually be sent the SMS? Reachable by SMS (a number on
 * file, not opted out) and not muted for this event — the only parents whose
 * text has to fit in one segment. */
export const willGetSms = (p) => !p?.muted && p?.reach?.sms === 'ok';

const CENTRAL_TZ = 'America/Chicago';

// SCRUM-433: the preview shows a deliberate spread so every placeholder's
// fallback is visible — not whoever happened to sort first.
const SAMPLE_LABEL_HINT = {
  fullest: 'The recipient whose message carries the most real data',
  middle: 'A typical recipient',
  sparsest: 'The recipient with the least data — every fallback visible',
};

// Minutes a timezone's wall clock sits from true UTC at a given instant
// (negative west of UTC, e.g. -300 for CDT). Standard Intl-based trick:
// format the instant in the target zone, re-read those wall-clock fields as
// if they were UTC, and diff against the real instant.
function timeZoneOffsetMinutes(timeZone, date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  const asUTC = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second)
  );
  return (asUTC - date.getTime()) / 60000;
}

// `<input type="date">` gives a bare 'YYYY-MM-DD'; sent as-is the API parses
// it as UTC midnight, which is 5-6 hours off from what a Central-time
// operator means. These convert to the true UTC instant for Central
// midnight / end-of-day on that date (DST-aware — CDT in summer, CST in
// winter), for `signedUpAfter` / `signedUpBefore`.
//
// The offset is computed from a whole-second reference (ms stripped) even
// when the target instant itself carries milliseconds (centralDayEnd's
// :999) — milliseconds can't shift a timezone's minute offset, and feeding
// them into timeZoneOffsetMinutes's Intl round-trip (which only has
// second resolution) introduces a sub-second rounding error.
function centralInstant(dateStr, hour, minute, second, ms) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const asIfUTC = Date.UTC(y, m - 1, d, hour, minute, second, ms);
  const refForOffset = Date.UTC(y, m - 1, d, hour, minute, second, 0);
  const offsetMin = timeZoneOffsetMinutes(CENTRAL_TZ, new Date(refForOffset));
  return new Date(asIfUTC - offsetMin * 60000).toISOString();
}

function centralDayStart(dateStr) {
  return centralInstant(dateStr, 0, 0, 0, 0);
}

function centralDayEnd(dateStr) {
  return centralInstant(dateStr, 23, 59, 59, 999);
}

// Exported for unit testing only — everything else in this module is
// consumed through the default-exported component.
export { centralDayStart, centralDayEnd };

const fmtCentral = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

// No year — a purchase is always this event, always this season. e.g. "Sep 13, 8:24 PM".
const fmtBoughtAt = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

/**
 * Props: { eventId, stages: string[], onClose, onSent, onSelectAthlete }
 * Parent (EventOps.jsx) should mount this with `key={stages.join('+')}` so
 * switching the selected funnel stages resets all local state instead of
 * reusing it. `onSelectAthlete(attendeeId)` — clicking an athlete chip or a
 * parent's name jumps the roster/photo grid to that athlete; the builder
 * stays open and the selected stages are untouched.
 */
export default function AudienceBuilder({ eventId, eventName = '', eventSlug = null, stages, onClose, onSent, onSelectAthlete }) {
  // SCRUM-356 Task 15 follow-up: once "purchased" is one of the selected
  // stages, the table gains a Bought column and defaults to newest-purchase
  // sort instead of the server's name order.
  const hasPurchased = stages.includes('purchased');

  const [signedUpAfter, setSignedUpAfter] = useState('');
  const [signedUpBefore, setSignedUpBefore] = useState('');
  // Any combination of push/sms/email.
  const [channelPush, setChannelPush] = useState(true);
  const [channelSms, setChannelSms] = useState(true);
  const [channelEmail, setChannelEmail] = useState(false);
  // Meaningful only when both push and sms are selected — sms is sent only
  // to parents whose push leg is dead/absent.
  const [smsFallback, setSmsFallback] = useState(true);
  const [excluded, setExcluded] = useState(() => new Set());
  // SCRUM-449: title/subject/per-channel bodies live in the shared
  // MessageEditor; every send is its own run, so there is no campaign key.
  const [message, setMessage] = useState({ title: '', subject: '', bodies: { push: '', sms: '', email: '' } });
  // SCRUM-449: the editor reports an SMS that runs past one text — Send stays off.
  const [smsTooLong, setSmsTooLong] = useState(false);
  // Default: newest first — by purchase time when Purchased is selected,
  // otherwise by signup date (the API carries no "entered this stage"
  // timestamp yet, so signup is the closest proxy until it does).
  const [sortMode, setSortMode] = useState(() => (hasPurchased ? 'purchase' : 'signup'));

  const [audience, setAudience] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(true);
  const [deviceCheck, setDeviceCheck] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sendReport, setSendReport] = useState(null);
  // Reset on ANY input change (filters or template); a Preview only clears it
  // for the exact inputs it ran against — Send is gated on this being false.
  const [dirty, setDirty] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const seqRef = useRef(0);

  const channels = useMemo(() => {
    const list = [];
    if (channelPush) list.push('push');
    if (channelSms) list.push('sms');
    if (channelEmail) list.push('email');
    return list;
  }, [channelPush, channelSms, channelEmail]);

  const filters = useMemo(
    () => ({
      stages,
      channels,
      smsFallback,
      signedUpAfter: signedUpAfter ? centralDayStart(signedUpAfter) : undefined,
      signedUpBefore: signedUpBefore ? centralDayEnd(signedUpBefore) : undefined,
      excludeParentIds: excluded.size ? [...excluded] : undefined,
    }),
    [stages, channels, smsFallback, signedUpAfter, signedUpBefore, excluded]
  );

  // Any input change invalidates the last Preview — Send is gated on `dirty`
  // being false, and the (now stale) report is hidden rather than left
  // showing numbers that no longer match the current inputs.
  const markDirty = () => {
    setDirty(true);
    setPreview(null);
    setSendReport(null);
  };

  // Sequence-guarded audience fetch — shared by the debounced filter effect
  // AND the post-check-devices refetch, so neither can let a slower, older
  // response clobber a fresher one (both write the same `audience` state).
  const loadAudience = (f) => {
    const seq = ++seqRef.current;
    setAudienceLoading(true);
    return getCampaignAudience(eventId, f)
      .then((data) => {
        if (seqRef.current !== seq) return;
        setAudience(data);
        setError('');
      })
      .catch((e) => {
        if (seqRef.current !== seq) return;
        setError(e.message);
      })
      .finally(() => {
        if (seqRef.current === seq) setAudienceLoading(false);
      });
  };

  // Debounced audience refetch whenever a filter (incl. manual exclusions)
  // changes.
  useEffect(() => {
    markDirty();
    const handle = setTimeout(() => { loadAudience(filters); }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, filters]);

  const toggleExclude = (parentId) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const handleCheckDevices = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await checkCampaignDevices(eventId);
      setDeviceCheck(result);
      // Reachability may have just moved (a push device found dead, etc.) —
      // any Preview already run is stale against the refreshed audience, so
      // require a new one before Send re-enables.
      markDirty();
      await loadAudience(filters);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // SCRUM-449: Send creates a run and returns its id at once; the server
  // works through it in the background. Poll the run every 3 s for the
  // banner until its status is no longer 'running'. Closing the tab never
  // stops a send — the run is the source of truth either way.
  const [progress, setProgress] = useState(null); // { sent, failed, other, startedAt, runId, stopped } | null
  const [finished, setFinished] = useState(null); // { status, reason, seconds, runId } | null
  const progressTimer = useRef(null);
  const startedAtRef = useRef(0);
  const stopProgress = () => {
    if (progressTimer.current) clearInterval(progressTimer.current);
    progressTimer.current = null;
  };
  useEffect(() => stopProgress, []);

  const finishRun = (run, runId) => {
    stopProgress();
    setProgress(null);
    setSendReport(run.counts ?? {});
    setFinished({
      status: run.status,
      reason: run.reason,
      seconds: Math.round((Date.now() - startedAtRef.current) / 1000),
      runId: run.id ?? runId,
    });
    setBusy(false);
    if (run.status === 'done') playKaching();
    onSent?.();
  };

  const pollRun = async (runId) => {
    try {
      const data = await getCampaignRun(eventId, runId);
      const run = data?.run;
      if (!run) return;
      if (run.status !== 'running') {
        finishRun(run, runId);
        return;
      }
      const rows = (data.rows ?? []).filter((r) => !r.isMonitor);
      const stopped = Date.now() - new Date(run.updatedAt).getTime() > RUN_STALE_MS;
      if (stopped) {
        stopProgress();
        setBusy(false);
      }
      setProgress({
        sent: rows.filter((r) => r.status === 'sent').length,
        failed: rows.filter((r) => r.status === 'failed').length,
        other: rows.filter((r) => !['sent', 'failed', 'pending'].includes(r.status)).length,
        startedAt: startedAtRef.current,
        runId,
        stopped,
      });
    } catch {
      // transient — keep the last counts
    }
  };

  const pollEvery = (runId) => {
    stopProgress();
    progressTimer.current = setInterval(() => pollRun(runId), POLL_MS);
  };

  // Resume a run that stopped (no progress) or ended 'failed'/'stopped'. The
  // elapsed timer restarts: it measures this attempt, not the first one.
  const resumeRun = async (fromRunId, counts) => {
    if (!fromRunId) return;
    setError('');
    try {
      const { runId } = await resumeCampaignRun(eventId, fromRunId);
      const id = runId ?? fromRunId;
      startedAtRef.current = Date.now();
      setBusy(true);
      setFinished(null);
      setSendReport(null);
      setProgress({ ...counts, startedAt: startedAtRef.current, runId: id, stopped: false });
      pollEvery(id);
    } catch (e) {
      setError(e.message);
    }
  };
  const handleResume = () =>
    resumeRun(progress?.runId, { sent: progress?.sent ?? 0, failed: progress?.failed ?? 0, other: progress?.other ?? 0 });
  const handleResumeFinished = () => resumeRun(finished?.runId, { sent: 0, failed: 0, other: 0 });

  const [, tick] = useState(0);
  useEffect(() => {
    if (!progress || progress.stopped) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [progress]);

  // Fallback body for any channel without its own text (the API's rule).
  const firstBody = channels.map((ch) => message.bodies[ch]).find((b) => b?.trim()) ?? '';

  const runSend = async (dryRun) => {
    setBusy(true);
    setError('');
    const channelBodies = Object.fromEntries(
      channels.filter((ch) => message.bodies[ch]?.trim()).map((ch) => [ch, message.bodies[ch]])
    );
    const payload = {
      filters,
      title: channelPush ? message.title || undefined : undefined,
      subject: channelEmail ? message.subject : undefined,
      body: firstBody,
      bodies: channelBodies,
      dryRun,
    };
    if (dryRun) {
      try {
        const report = await sendCampaign(eventId, payload);
        setPreview(report);
        setDirty(false);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    startedAtRef.current = Date.now();
    setFinished(null);
    setProgress({ sent: 0, failed: 0, other: 0, startedAt: startedAtRef.current, runId: null, stopped: false });
    try {
      const { runId } = await sendCampaign(eventId, payload);
      setPreview(null);
      setDirty(true);
      setProgress((p) => ({ ...p, runId }));
      pollEvery(runId);
    } catch (e) {
      setError(e.message);
      stopProgress();
      setProgress(null);
      setBusy(false);
    }
  };

  const handlePreview = () => runSend(true);

  const handleSend = async () => {
    if (dirty || !preview || busy || smsTooLong) return;
    const first = preview.sample?.[0];
    const samples = first ? preview.sample.filter((x) => x.parent === first.parent) : [];
    const confirmed = window.confirm(
      `Send to ${preview.audience} recipient(s) over ${channels.join('+')}?` +
        (first
          ? `\n\nSample (${first.parent}):\n` +
            samples.map((x) => `${x.channel ? `[${x.channel}] ` : ''}${x.body}`).join('\n\n')
          : '')
    );
    if (!confirmed) return;
    await runSend(false);
  };

  const parents = useMemo(() => audience?.parents ?? [], [audience]);
  const excludedStats = audience?.excluded;
  const messageReady = channels.length > 0 && channels.every((ch) => (message.bodies[ch] || firstBody).trim());

  // Contact history popover on the Msgs column: every message this parent
  // got about this event, auto and custom, newest first.
  const [history, setHistory] = useState(null); // { parentId, rows: [] | null, error } | null
  const openHistory = async (parentId) => {
    if (history?.parentId === parentId) {
      setHistory(null);
      return;
    }
    setHistory({ parentId, rows: null, error: '' });
    try {
      const rows = await getParentCampaignHistory(eventId, parentId);
      setHistory((h) => (h?.parentId === parentId ? { parentId, rows: rows ?? [], error: '' } : h));
    } catch (e) {
      setHistory((h) => (h?.parentId === parentId ? { parentId, rows: [], error: e.message } : h));
    }
  };

  // Per-event mute: the parent stays listed but gets nothing more about this
  // event, auto or custom, on any channel.
  const toggleMute = async (p) => {
    setError('');
    try {
      await setParentMute(eventId, p.id, !p.muted);
      markDirty();
      await loadAudience(filters);
    } catch (e) {
      setError(e.message);
    }
  };

  // "Newest purchase first" (rows with no boughtAt sort last) or plain
  // name order — server order is already by name, so "name" is a no-op
  // re-sort kept only for readability/symmetry.
  const sortedParents = useMemo(() => {
    const list = [...parents];
    const ts = (v) => (v ? new Date(v).getTime() : -Infinity);
    if (hasPurchased && sortMode === 'purchase') {
      list.sort((a, b) => ts(b.boughtAt) - ts(a.boughtAt));
    } else if (sortMode === 'signup') {
      list.sort((a, b) => ts(b.createdAt) - ts(a.createdAt));
    } else {
      list.sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''));
    }
    return list;
  }, [parents, sortMode, hasPurchased]);


  // Explain the count as an equation: N reachable of M at the stage. Unreachable,
  // opted-out and muted parents stay in the table (their ✗ per channel or 🔕
  // says why); only the operator's own removals are named.
  const channelSummary = channels.map((c) => ({ push: 'push', sms: 'SMS', email: 'email' }[c] ?? c)).join('/');
  const reachableCount = parents.filter((p) => p.reachable !== false).length;
  const smsParents = parents.filter(willGetSms);
  const excludedNamed = (audience?.excludedParents ?? []).slice(0, 8);
  const reachTitle = (reach) =>
    reach ? `push: ${reach.push} · SMS: ${reach.sms} · email: ${reach.email}` : '';
  return (
    <div className={styles.audienceBuilder}>
      <div className={styles.audienceHeader}>
        <h3>
          Audience
          <span className={styles.chips}>
            {stages.map((s) => (
              <span key={s} className={styles.chip}>{BUILDER_STAGE_LABELS[s] || s}</span>
            ))}
          </span>
        </h3>
        <button className={styles.closeBtn} onClick={onClose}>Close</button>
      </div>

      {error && <div className={styles.status}>{error}</div>}

      <div className={styles.audienceFilters}>
        <label>
          Signed up after
          <input
            type="date"
            value={signedUpAfter}
            onChange={(e) => setSignedUpAfter(e.target.value)}
          />
        </label>
        <label>
          Signed up before
          <input
            type="date"
            value={signedUpBefore}
            onChange={(e) => setSignedUpBefore(e.target.value)}
          />
        </label>
        <div className={styles.channelGroup}>
          <span className={styles.channelGroupLabel}>Channels</span>
          <ChannelPicker
            value={channels}
            allowed={CHANNELS}
            onChange={(list) => {
              setChannelPush(list.includes('push'));
              setChannelSms(list.includes('sms'));
              setChannelEmail(list.includes('email'));
              markDirty();
            }}
          />
          {channelPush && channelSms && (
            <label className={styles.channelOption}>
              <input
                type="checkbox"
                checked={smsFallback}
                onChange={(e) => { setSmsFallback(e.target.checked); markDirty(); }}
              />
              SMS only if push fails
            </label>
          )}
        </div>
        <button type="button" onClick={handleCheckDevices} disabled={busy}>
          {busy ? 'Working…' : 'Check devices'}
        </button>
      </div>

      {deviceCheck && (
        <div className={styles.subtle}>
          Devices checked {fmtCentral(deviceCheck.checkedAt)}: {deviceCheck.valid} valid ·{' '}
          {deviceCheck.invalid} invalid · {deviceCheck.noDevice} no device
          {deviceCheck.cached ? ' (cached)' : ''}
        </div>
      )}

      <div className={styles.audienceCount}>
        {audienceLoading ? (
          <strong>Loading…</strong>
        ) : (
          <>
            <strong>{reachableCount} reachable</strong>
            {audience?.atStage != null && (
              <span className={styles.subtle}> of {audience.atStage} at {stages.map((s) => BUILDER_STAGE_LABELS[s] ?? s).join(' + ')}</span>
            )}
          </>
        )}
        {excludedStats && (
          <span className={styles.chips}>
            {excludedStats.buyers > 0 && (
              <span className={styles.chip} title="Buyers sit at the Purchased stage; they are never part of an earlier stage's audience">
                {excludedStats.buyers} already bought
              </span>
            )}
            {excludedStats.unreachable > 0 && (
              <span className={styles.chip}>{excludedStats.unreachable} unreachable on {channelSummary}</span>
            )}
            {excludedStats.optedOut > 0 && (
              <span className={styles.chip}>{excludedStats.optedOut} opted out</span>
            )}
            {excludedStats.muted > 0 && (
              <span className={styles.chip} title="Replied MUTE or muted by ops — nothing more about this event">
                {excludedStats.muted} muted for this event
              </span>
            )}
            {excludedStats.manual > 0 && (
              <span className={`${styles.chip} ${styles.chipManual}`}>
                {excludedStats.manual} manually removed
              </span>
            )}
          </span>
        )}
        {excludedNamed.length > 0 && (
          <div className={styles.excludedNames}>
            {excludedNamed.map((x) => (
              <span key={x.id} className={styles.excludedName} title={reachTitle(x.reach)}>
                {x.fullName || 'no name'} — {EXCLUDED_REASON[x.reason] ?? x.reason}
              </span>
            ))}
          </div>
        )}
      </div>

      <ReachLegend />
      <div className={styles.audienceFilters}>
        <label>
          Sort
          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
            {hasPurchased && <option value="purchase">Newest purchase first</option>}
            <option value="signup">Newest signup first</option>
            <option value="name">Name</option>
          </select>
        </label>
      </div>
      <table className={styles.campaignTable}>
        <thead>
          <tr>
            <th>Parent</th>
            <th>Reach</th>
            <th>Msgs</th>
            <th>Athletes</th>
            {hasPurchased && <th>Bought</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sortedParents.map((p) => {
            const firstAthleteId = p.athletes?.[0]?.id;
            const rowClass = p.muted ? styles.mutedRow : p.reachable === false ? styles.unreachableRow : '';
            return (
            <tr key={p.id} className={rowClass}>
              <td>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => { if (firstAthleteId) onSelectAthlete?.(firstAthleteId); }}
                >
                  {p.fullName || <span className={styles.subtle}>no name</span>}
                </button>
                {p.muted && <span title="Muted for this event"> 🔕</span>}
              </td>
              <td>
                <ReachIndicators reach={p.reach} />
              </td>
              <td className={styles.msgsCell}>
                <button
                  type="button"
                  className={styles.linkButton}
                  title="Contact history"
                  onClick={() => openHistory(p.id)}
                >
                  📱 {p.messages?.push ?? 0} 💬 {p.messages?.sms ?? 0} ✉ {p.messages?.email ?? 0}
                </button>
                {history?.parentId === p.id && (
                  <div
                    className={styles.historyPopover}
                    role="dialog"
                    aria-label={`Contact history — ${p.fullName || 'no name'}`}
                  >
                    <strong>Contact history</strong>{' '}
                    <button type="button" className={styles.chipRemove} title="Close" onClick={() => setHistory(null)}>
                      ✕
                    </button>
                    {history.error && <div className={styles.status}>{history.error}</div>}
                    {history.rows === null ? (
                      <div className={styles.subtle}>Loading…</div>
                    ) : history.rows.length === 0 ? (
                      <div className={styles.subtle}>No messages about this event yet</div>
                    ) : (
                      <ul>
                        {history.rows.map((h, i) => (
                          <li key={`${h.runId}-${h.channel}-${i}`} className={h.status === 'failed' ? styles.historyFailed : ''}>
                            {h.label} · <span title={CHANNEL_LABEL[h.channel]}>{CHANNEL_ICON[h.channel] ?? h.channel}</span>
                            {' · '}{h.status} · {fmtBoughtAt(h.at)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </td>
              <td>
                {(p.athletes ?? []).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={styles.athleteChip}
                    onClick={() => onSelectAthlete?.(a.id)}
                  >
                    {(a.fullName || 'unnamed').trim()} ({a.matchedPhotos})
                  </button>
                ))}
                {!hasPurchased && p.bought && <span className={styles.chip}>bought: {p.bought}</span>}
              </td>
              {hasPurchased && (
                <td>
                  {p.bought ? `${p.bought}${p.boughtAt ? ` · ${fmtBoughtAt(p.boughtAt)}` : ''}` : ''}
                </td>
              )}
              <td>
                <button
                  type="button"
                  className={`${styles.linkButton} ${styles.muteLink}`}
                  title={p.muted ? 'Let this parent hear about this event again' : 'Send this parent nothing more about this event'}
                  onClick={() => toggleMute(p)}
                >
                  {p.muted ? 'Unmute' : 'Mute event'}
                </button>
                <button
                  className={styles.chipRemove}
                  title="Remove from this send"
                  onClick={() => toggleExclude(p.id)}
                >
                  ✕
                </button>
              </td>
            </tr>
            );
          })}
          {parents.length === 0 && !audienceLoading && (
            <tr>
              <td colSpan={hasPurchased ? 6 : 5} className={styles.subtle}>No parents match these filters</td>
            </tr>
          )}
        </tbody>
      </table>

      <MessageEditor
        channels={channels}
        title={message.title}
        subject={message.subject}
        bodies={message.bodies}
        onChange={(m) => { setMessage(m); markDirty(); }}
        // SCRUM-449 follow-up: a lead recipient's {app} is the event's own
        // link now too (or the generic one when it has no slug) — only
        // their contact line is still STOP-only (per parent, by `p.stage`);
        // a lead-only audience with nobody in it yet sizes on that via
        // `lead: isLeadOnly(stages)`. Sized only over parents who will
        // really get a text: reachable by SMS, not muted, not opted out.
        lengthContext={{
          parents: smsParents,
          eventName,
          app: appLink(eventSlug, 'sms'),
          lead: isLeadOnly(stages),
        }}
        onValidityChange={setSmsTooLong}
        audienceAware
      />

      <div className={styles.audienceActions}>
        <button
          type="button"
          onClick={handlePreview}
          disabled={busy || !messageReady || (channelEmail && !message.subject.trim())}
        >
          {busy ? 'Working…' : 'Preview'}
        </button>
        <button type="button" onClick={handleSend} disabled={busy || dirty || !preview || smsTooLong}>
          Send
        </button>
      </div>

      {preview && (
        <div className={styles.audienceReport}>
          <strong>Preview (dry run)</strong> — audience {preview.audience}: sent {preview.sent},
          {' '}failed {preview.failed}, refused {preview.refused}, skipped {preview.skippedAlreadySent},
          {' '}optOut {preview.optOut}, noContact {preview.noContact}, monitors sent {preview.monitorsSent},
          {' '}monitors failed {preview.monitorsFailed}
          {preview.sample?.length > 0 && (
            <ul>
              {preview.sample.map((s, i) => (
                <li key={i}>
                  {s.label && (
                    <span className={styles.chip} title={SAMPLE_LABEL_HINT[s.label]}>{s.label}</span>
                  )}{' '}
                  {s.channel ? `${CHANNEL_LABEL[s.channel] ?? s.channel} · ` : ''}{s.parent}: {s.body}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {progress && (
        <div className={`${styles.audienceReport} ${styles.sendProgress}`} role="status">
          <strong>{progress.stopped ? 'Stopped' : 'Sending…'}</strong> {progress.sent} sent · {progress.failed} failed
          {progress.other > 0 ? ` · ${progress.other} skipped/refused/opt-out` : ''}
          {!progress.stopped && <> · {Math.round((Date.now() - progress.startedAt) / 1000)} s</>}
          {progress.stopped ? (
            <div>
              No progress for 2 minutes.{' '}
              <button type="button" onClick={handleResume}>Resume</button>
            </div>
          ) : (
            <div className={styles.subtle}>
              Safe to navigate away — the server keeps sending; the run lands in the event's campaign history.
            </div>
          )}
        </div>
      )}
      {sendReport && (
        <div className={`${styles.audienceReport} ${finished?.status === 'done' ? styles.sendDone : ''}`} role="status">
          <strong>
            {finished?.status === 'done'
              ? `✅ Done in ${finished.seconds} s`
              : finished
                ? `❌ ${finished.status}${finished.reason ? `: ${finished.reason}` : ''}`
                : 'Sent'}
          </strong>{' '}
          — audience {sendReport.audience ?? 0}: sent {sendReport.sent ?? 0},
          {' '}failed {sendReport.failed ?? 0}, refused {sendReport.refused ?? 0}, skipped {sendReport.skippedAlreadySent ?? 0},
          {' '}optOut {sendReport.optOut ?? 0}, noContact {sendReport.noContact ?? 0}, monitors sent {sendReport.monitorsSent ?? 0},
          {' '}monitors failed {sendReport.monitorsFailed ?? 0}
          {finished && RESUMABLE.has(finished.status) && finished.runId && (
            <div>
              <button type="button" onClick={handleResumeFinished}>Resume</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
